import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ANIMATION_STATES } from './AuraRobot';
import VoiceOutput from './VoiceOutput';
import ConversationHistory from './ConversationHistory';
import ProactiveAlerts from './ProactiveAlerts';
import EnableSoundButton from './EnableSoundButton';
import MuteButton from './MuteButton';
import useAuraWebSocket from '../../hooks/useAuraWebSocket';
import { useAudioStore } from '../../stores/audioStore';
import { speechEngine } from '../../utils/SpeechEngine';
import api from '../../api';
import { getRandomQuestions } from './predefinedQuestions';

export default function AuraWidget({ lastSignalData }) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [animationState, setAnimationState] = useState(ANIMATION_STATES.IDLE);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isProcessingQuery, setIsProcessingQuery] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'summary'
  const [textInput, setTextInput] = useState(''); // manual text command input
  const conversationEndRef = useRef(null);
  const hasAttemptedGreetingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile screen size for responsive positioning
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Get audio store functions
  const { speak } = useAudioStore();
  
  // State for tracking if welcome message has been spoken
  const [hasSpokenWelcome, setHasSpokenWelcome] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const idleVideoSrc = '/models/silent_idle.mp4';
  const idleVideoSrcWebm = '/models/silent_idle.webm';
  const talkingVideoSrc = '/models/talking.mp4';
  const talkingVideoSrcWebm = '/models/talking.webm';
  const posterSrc = '/models/poster.jpg';
  
  // Define handleAlert before using it in the hook
  const handleAlert = (alert) => {
    if (alert && alert.message) {
      // Check if this is a welcome/connection message
      const isWelcomeMessage = alert.type === 'connection' || 
                               alert.message.toLowerCase().includes('hello') ||
                               alert.message.toLowerCase().includes('connected');
      
      // Add alert to conversation history
      const alertMessage = {
        type: 'aura',
        text: alert.message,
        timestamp: new Date().toISOString()
      };
      
      // Note: Welcome messages are now handled by the isolated useEffect below
      // This handler only processes non-welcome alerts
      if (!isWelcomeMessage) {
        // Regular alert - append to conversation
        const alertText = alert.voice_script || alert.text || alert.message;
        setConversation(prev => [...prev, alertMessage]);
        setCurrentMessage(alertText);
        setAnimationState(alert.priority === 'high' ? ANIMATION_STATES.ALERT : ANIMATION_STATES.SPEAKING);
        speak(alertText); // Use the speech engine
      }
    }
  };
  
  // Use the WebSocket hook to get the welcome message and handle alerts
  // This hook manages the WebSocket connection and exposes the welcome message
  const { auraWelcomeMessage } = useAuraWebSocket(handleAlert, true);

  // CRITICAL: Handle welcome message ONCE - decoupled from render cycle
  useEffect(() => {
    // If we have a welcome message AND we haven't spoken it yet
    if (auraWelcomeMessage && !hasSpokenWelcome) {
      console.log('👋 AURA welcome message received, setting up speech');
      setHasSpokenWelcome(true); // Lock it, so it never speaks again
      setHasGreeted(true);
      
      // Update UI state
      setCurrentMessage(auraWelcomeMessage);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      
      // Add to conversation history
      setConversation([{
        type: 'aura',
        text: auraWelcomeMessage,
        timestamp: new Date().toISOString()
      }]);
      
      // Speak the welcome message (will only play if not muted)
      speak(auraWelcomeMessage);
    }
  }, [auraWelcomeMessage, hasSpokenWelcome, speak]);

  // Fetch market summary on mount (fallback if WebSocket welcome doesn't come)
  useEffect(() => {
    // Only run once on mount
    if (hasAttemptedGreetingRef.current) return;
    hasAttemptedGreetingRef.current = true;
    
    // Wait a bit for WebSocket welcome message to arrive first
    const welcomeTimeout = setTimeout(() => {
      // Check current state using a function to get latest value
      setHasGreeted(currentHasGreeted => {
        if (!currentHasGreeted) {
          console.log('📢 WebSocket welcome not received, fetching market summary via API...');
          fetchMarketSummary();
          return true;
        }
        return currentHasGreeted;
      });
    }, 2000); // Wait 2 seconds for WebSocket welcome message
    
    return () => clearTimeout(welcomeTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Scroll to bottom of conversation when new messages arrive
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Preload videos for instant switching between silent/talking loops
  useEffect(() => {
    const idleVideo = document.createElement('video');
    const talkingVideo = document.createElement('video');
    
    // Add both mp4 and webm sources for better browser compatibility
    const idleSourceMp4 = document.createElement('source');
    idleSourceMp4.src = idleVideoSrc;
    idleSourceMp4.type = 'video/mp4';
    const idleSourceWebm = document.createElement('source');
    idleSourceWebm.src = idleVideoSrcWebm;
    idleSourceWebm.type = 'video/webm';
    idleVideo.appendChild(idleSourceMp4);
    idleVideo.appendChild(idleSourceWebm);
    
    const talkingSourceMp4 = document.createElement('source');
    talkingSourceMp4.src = talkingVideoSrc;
    talkingSourceMp4.type = 'video/mp4';
    const talkingSourceWebm = document.createElement('source');
    talkingSourceWebm.src = talkingVideoSrcWebm;
    talkingSourceWebm.type = 'video/webm';
    talkingVideo.appendChild(talkingSourceMp4);
    talkingVideo.appendChild(talkingSourceWebm);
    
    idleVideo.preload = 'auto';
    talkingVideo.preload = 'auto';

    let isMounted = true;
    const handleCanPlay = () => {
      if (isMounted) setVideoReady(true);
    };

    idleVideo.addEventListener('canplaythrough', handleCanPlay, { once: true });
    talkingVideo.addEventListener('canplaythrough', handleCanPlay, { once: true });

    return () => {
      isMounted = false;
      idleVideo.removeEventListener('canplaythrough', handleCanPlay);
      talkingVideo.removeEventListener('canplaythrough', handleCanPlay);
    };
  }, []);

  // Sync video state with speech engine start/end (OpenAI TTS)
  useEffect(() => {
    const handleSpeechStart = () => {
      setAnimationState(ANIMATION_STATES.SPEAKING);
    };
    const handleSpeechEnd = () => {
      // Only drop to idle if not actively processing a voice query
      setAnimationState((prev) => {
        if (prev === ANIMATION_STATES.THINKING || prev === ANIMATION_STATES.LISTENING) {
          return prev;
        }
        return ANIMATION_STATES.IDLE;
      });
    };

    speechEngine.addOnStartListener(handleSpeechStart);
    speechEngine.addOnEndListener(handleSpeechEnd);

    return () => {
      speechEngine.removeOnStartListener(handleSpeechStart);
      speechEngine.removeOnEndListener(handleSpeechEnd);
    };
  }, []);

  // Update when signal data changes (for proactive alerts)
  useEffect(() => {
    if (lastSignalData) {
      // Proactive alerts will handle this via the ProactiveAlerts component
    }
  }, [lastSignalData]);

  const fetchMarketSummary = async () => {
    setIsLoading(true);
    setAnimationState(ANIMATION_STATES.THINKING);
    
    try {
      const response = await api.get('/api/v1/robot/market-summary');
      const summary = response.data;
      
      // Get the full voice script - ensure we use the complete text
      const greetingMessage = summary.voice_script || summary.text || "Hello! I'm AURA, your trading assistant. I'm ready to help you navigate the market.";
      
      // Ensure we have the full message (not truncated)
      const fullMessage = typeof greetingMessage === 'string' ? greetingMessage : JSON.stringify(greetingMessage);
      
      console.log('Market summary received:', { 
        voice_script_length: summary.voice_script?.length || 0,
        text_length: summary.text?.length || 0,
        fullMessage_length: fullMessage.length 
      });
      
      setCurrentMessage(fullMessage);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      
      // Add greeting to conversation history
      setConversation([{
        type: 'aura',
        text: greetingMessage,
        timestamp: new Date().toISOString()
      }]);
      
      // Speak the greeting (will only play if not muted)
      speak(greetingMessage);
    } catch (error) {
      console.error('Failed to fetch market summary:', error);
      const fallbackGreeting = "Hello! I'm AURA, your trading assistant. I'm ready to help you navigate the market.";
      setCurrentMessage(fallbackGreeting);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      
      // Add fallback greeting to conversation history
      setConversation([{
        type: 'aura',
        text: fallbackGreeting,
        timestamp: new Date().toISOString()
      }]);
      
      // Speak the fallback greeting (will only play if not muted)
      speak(fallbackGreeting);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpeakingComplete = () => {
    setAnimationState(ANIMATION_STATES.IDLE);
  };

  const handleRefresh = () => {
    fetchMarketSummary();
  };

  const handleGetPrediction = async () => {
    setIsLoading(true);
    setAnimationState(ANIMATION_STATES.THINKING);
    
    try {
      // Changed from GET to POST as backend expects POST
      const response = await api.post('/api/v1/robot/predict');
      const prediction = response.data;
      
      // Backend returns {text, voice_script, timestamp}
      const predictionText = prediction.voice_script || prediction.text || "No prediction available.";
      
      const predictionMessage = {
        type: 'aura',
        text: predictionText,
        timestamp: new Date().toISOString()
      };
      
      setConversation(prev => [...prev, predictionMessage]);
      setCurrentMessage(predictionMessage.text);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      speak(predictionMessage.text); // Use the speech engine
      
    } catch (error) {
      console.error('Error getting prediction:', error);
      const errorMessage = {
        type: 'aura',
        text: "I'm sorry, I couldn't generate a prediction right now. Please try again.",
        timestamp: new Date().toISOString()
      };
      setConversation(prev => [...prev, errorMessage]);
      setCurrentMessage(errorMessage.text);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      speak(errorMessage.text); // Use the speech engine
    } finally {
      setIsLoading(false);
    }
  };

  const handleVoiceTranscript = async (transcript) => {
    if (!transcript || transcript.trim() === '') return;

    console.log('🎤 Processing voice command:', transcript);

    // Add user message to conversation
    const userMessage = {
      type: 'user',
      text: transcript,
      timestamp: new Date().toISOString()
    };
    setConversation(prev => [...prev, userMessage]);
    
    // Set animation to listening, then thinking
    setAnimationState(ANIMATION_STATES.LISTENING);
    setIsProcessingQuery(true);
    
    // Brief delay to show listening animation
    await new Promise(resolve => setTimeout(resolve, 300));
    setAnimationState(ANIMATION_STATES.THINKING);
    
    try {
      // Prepare conversation history (format for backend)
      const conversationHistory = conversation.map(msg => ({
        type: msg.type,
        text: msg.text
      }));
      
      console.log('📤 Sending voice command to backend...');
      
      // Send query to backend with conversation history
      const response = await api.post('/api/v1/robot/chat', {
        query: transcript,
        conversation_history: conversationHistory
      });
      
      const auraResponse = response.data.response;
      
      console.log('✅ Received response from AURA:', auraResponse.substring(0, 50) + '...');
      
      // Add AURA's response to conversation
      const auraMessage = {
        type: 'aura',
        text: auraResponse,
        timestamp: new Date().toISOString()
      };
      setConversation(prev => [...prev, auraMessage]);
      
      // Update UI immediately with text response (user sees it right away)
      setCurrentMessage(auraResponse);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      
      // Start TTS generation in parallel (non-blocking)
      // Text is already visible, audio will play when ready
      speak(auraResponse); // Use the speech engine (queued, non-blocking)
      
    } catch (error) {
      console.error('❌ Error processing voice command:', error);
      const errorMessage = {
        type: 'aura',
        text: "I'm sorry, I encountered an error processing your voice command. Please try again or rephrase your question.",
        timestamp: new Date().toISOString()
      };
      setConversation(prev => [...prev, errorMessage]);
      setCurrentMessage(errorMessage.text);
      setAnimationState(ANIMATION_STATES.SPEAKING);
      speak(errorMessage.text); // Use the speech engine
    } finally {
      setIsProcessingQuery(false);
    }
  };

  // Allow user to type a command instead of (or in addition to) using voice.
  // This reuses the same pipeline as voice: send to /api/v1/robot/chat and speak the reply.
  const handleTextSubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    const value = textInput.trim();
    if (!value) return;

    // Clear the input immediately for better UX
    setTextInput('');

    // Reuse the existing voice transcript handler so all logic (conversation,
    // backend call, TTS, animations) stays identical.
    await handleVoiceTranscript(value);
  };

  // Show reopen button if closed
  if (isClosed) {
    return (
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 15, stiffness: 200 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <button
          onClick={() => setIsClosed(false)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-full shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all hover:scale-110"
          title="Open AURA"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: 400, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 400, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-4 right-4 z-50 sm:bottom-4 sm:right-4"
        style={{ 
          maxWidth: 'min(400px, calc(100vw - 2rem))', // Responsive: 400px max, but adapts to viewport
          maxHeight: 'calc(100vh - 2rem)', // Ensure it doesn't exceed viewport height minus padding
          minHeight: 'auto',
          top: isMobile ? '1rem' : 'auto', // Position from top on mobile to prevent cutoff
          bottom: isMobile ? 'auto' : '1rem', // Position from bottom on desktop
          right: '1rem',
          left: 'auto'
        }}
      >
        <div className="bg-white rounded-lg shadow-2xl border-2 border-blue-500 overflow-hidden backdrop-blur-sm flex flex-col"
          style={{ 
            maxHeight: 'calc(100vh - 2rem)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header - Fixed at top */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <span className="text-white font-semibold">AURA</span>
              {isLoading && (
                <span className="text-white text-xs ml-2">Analyzing...</span>
              )}
            </div>
            <div className="flex gap-2">
              <MuteButton />
              <button
                onClick={handleRefresh}
                className="text-white hover:bg-white/20 p-1 rounded transition-colors"
                title="Refresh market summary"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="text-white hover:bg-white/20 p-1 rounded transition-colors"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? '↑' : '↓'}
              </button>
              <button
                onClick={() => setIsClosed(true)}
                className="text-white hover:bg-red-500/30 p-1 rounded transition-colors"
                title="Close AURA"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <AnimatePresence>
            {!isMinimized && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="p-4 overflow-y-auto flex-1"
                style={{ 
                  maxHeight: 'calc(100vh - 200px)', // Reserve space for header (~60px) and padding
                  minHeight: 0 // Allow flexbox to shrink if needed
                }}
              >
                {/* Tab Switcher */}
                <div className="flex gap-2 mb-4 border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('chat')}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'chat'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    💬 Chat
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('summary');
                      // Trigger speech when Summary tab is clicked
                      const summaryText = currentMessage || conversation.find(msg => msg.type === 'aura')?.text;
                      if (summaryText) {
                        // Force re-trigger speech by updating currentMessage
                        setCurrentMessage(''); // Clear first
                        setTimeout(() => {
                          setCurrentMessage(summaryText);
                          setAnimationState(ANIMATION_STATES.SPEAKING);
                        }, 100);
                      } else if (!hasGreeted) {
                        // If no message yet, fetch market summary
                        fetchMarketSummary();
                      }
                    }}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'summary'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📊 Summary
                  </button>
                </div>

                {/* Chat Tab */}
                {activeTab === 'chat' && (
                  <div className="space-y-4">
                    {/* AURA Video: idle vs talking */}
                    <div className="w-full h-48 bg-gradient-to-b from-blue-50 to-white rounded-lg overflow-hidden relative">
                      <video
                        key={animationState === ANIMATION_STATES.SPEAKING ? 'talking' : 'idle'}
                        poster={posterSrc}
                        className="w-full h-full object-cover"
                        playsInline
                        muted
                        loop
                        autoPlay
                        aria-label="AURA robot animation"
                      >
                        {animationState === ANIMATION_STATES.SPEAKING ? (
                          <>
                            <source src={talkingVideoSrc} type="video/mp4" />
                            <source src={talkingVideoSrcWebm} type="video/webm" />
                          </>
                        ) : (
                          <>
                            <source src={idleVideoSrc} type="video/mp4" />
                            <source src={idleVideoSrcWebm} type="video/webm" />
                          </>
                        )}
                      </video>
                      {!videoReady && (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                          Loading robot video...
                        </div>
                      )}
                    </div>

                {/* Conversation History */}
                <div className="bg-gray-50 rounded-lg p-2 max-h-64 overflow-y-auto">
                  <ConversationHistory 
                    messages={conversation} 
                    isLoading={isProcessingQuery}
                    onQuestionClick={handleVoiceTranscript}
                  />
                  <div ref={conversationEndRef} />
                </div>

                {/* Text Command Input - user will chat by typing; AURA will speak responses */}
                    <form onSubmit={handleTextSubmit} className="mt-3 flex items-center gap-2">
                      <input
                        type="text"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder="Type your question to AURA..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="submit"
                        disabled={isProcessingQuery || !textInput.trim()}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          isProcessingQuery || !textInput.trim()
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        title="Send your question to AURA"
                      >
                        Send
                      </button>
                    </form>

                    {/* Quick Actions */}
                    <div className="space-y-2 mt-3">
                      <div className="flex gap-2">
                        <button
                          onClick={handleGetPrediction}
                          disabled={isLoading}
                          className="flex-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-sm rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          title="Get AI market prediction"
                        >
                          🔮 Predict
                        </button>
                        <button
                          onClick={() => handleVoiceTranscript("What's the market doing?")}
                          disabled={isLoading}
                          className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          title="Quick market status"
                        >
                          📊 Status
                        </button>
                      </div>
                      
                      {/* Suggested Questions */}
                      {conversation.length === 0 && (
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500 font-medium px-1">Suggested Questions:</p>
                          <div className="flex flex-wrap gap-1.5">
                            {getRandomQuestions(4).map((question, index) => (
                              <button
                                key={index}
                                onClick={() => handleVoiceTranscript(question)}
                                disabled={isLoading}
                                className="text-xs px-2.5 py-1 bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-600 rounded-md transition-colors border border-gray-200 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Click to ask: ${question}`}
                              >
                                {question}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary Tab */}
                {activeTab === 'summary' && (
                  <div className="space-y-4">
                    {/* AURA Video: idle vs talking */}
                    <div className="w-full h-64 mb-4 bg-gradient-to-b from-blue-50 to-white rounded-lg overflow-hidden relative">
                      <video
                        key={animationState === ANIMATION_STATES.SPEAKING ? 'talking-summary' : 'idle-summary'}
                        poster={posterSrc}
                        className="w-full h-full object-cover"
                        playsInline
                        muted
                        loop
                        autoPlay
                        aria-label="AURA robot animation"
                      >
                        {animationState === ANIMATION_STATES.SPEAKING ? (
                          <>
                            <source src={talkingVideoSrc} type="video/mp4" />
                            <source src={talkingVideoSrcWebm} type="video/webm" />
                          </>
                        ) : (
                          <>
                            <source src={idleVideoSrc} type="video/mp4" />
                            <source src={idleVideoSrcWebm} type="video/webm" />
                          </>
                        )}
                      </video>
                      {!videoReady && (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                          Loading robot video...
                        </div>
                      )}
                    </div>

                    {/* Voice Output - Only show subtitles, no duplicate text box */}
                    <VoiceOutput
                      text={currentMessage || conversation.find(msg => msg.type === 'aura')?.text || ''}
                      onSpeakingComplete={handleSpeakingComplete}
                      autoPlay={true}
                    />
                  </div>
                )}

                {/* Proactive Alerts (hidden component that triggers alerts via WebSocket) */}
                <ProactiveAlerts
                  onAlert={handleAlert}
                  lastSignalData={lastSignalData}
                  enabled={!isMinimized}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      
      {/* Enable Sound Button - shown when audio is muted */}
      <EnableSoundButton />
    </AnimatePresence>
  );
}

